import { useState, useEffect, useMemo, useRef, type FormEvent } from "react";
import {
	useFetcher,
	useLoaderData,
	useNavigation,
	useRevalidator,
	useRouteError,
	useRouteLoaderData,
	isRouteErrorResponse,
} from "react-router";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { CreateDatasetDialog } from "@/components/CreateDatasetDialog";
import { SettingsDialog } from "@/components/SettingsDialog";
import type { Judge } from "@/lib/judges";
import type { Database } from "@/integrations/supabase/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";
import {
	AlertCircle,
	CheckCircle,
	Search,
	Download,
	Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { errorLogsLoader } from "@/routes/errorLogsRoute";
import { supabase } from "@shared/integrations/supabase/client";

type Project = Database["public"]["Tables"]["projects"]["Row"];
type Dataset = Database["public"]["Tables"]["test_data_sets"]["Row"];

interface ErrorLog {
	id: string;
	user_id: string;
	error_type: string;
	severity: string;
	source: string;
	error_message: string;
	error_details: string | null;
	http_status_code: number | null;
	context: unknown;
	request_payload: unknown;
	response_payload: unknown;
	created_at: string;
	resolved: boolean | null;
	resolved_at: string | null;
	resolution_notes: string | null;
}

export default function ErrorLogs() {
	const { logs } = useLoaderData<typeof errorLogsLoader>();
	const layoutData =
		useRouteLoaderData<WorkspaceLoaderData>(WORKSPACE_LOADER_ID);
	const user = layoutData?.user;
	const fetcher = useFetcher<{
		ok?: boolean;
		error?: string;
		logId?: string | null;
	}>();
	const resolveAllFetcher = useFetcher<{
		ok?: boolean;
		error?: string;
		resolvedCount?: number | null;
	}>();
	const navigation = useNavigation();
	const revalidator = useRevalidator();
	const [submittedLogId, setSubmittedLogId] = useState<string | null>(null);
	const lastHandledLogIdRef = useRef<string | null>(null);
	const [resolveAllSubmissionToken, setResolveAllSubmissionToken] = useState<
		number | null
	>(null);
	const lastResolveAllHandledTokenRef = useRef<number | null>(null);
	const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [severityFilter, setSeverityFilter] = useState<string>("all");
	const [sourceFilter, setSourceFilter] = useState<string>("all");
	const [resolvedFilter, setResolvedFilter] = useState<string>("all");
	const [createDatasetDialogOpen, setCreateDatasetDialogOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const projects = (layoutData?.projects ?? []) as Project[];
	const judges = (layoutData?.judges ?? []) as Judge[];
	const datasets = (layoutData?.datasets ?? []) as Dataset[];
	const codes = layoutData?.codes ?? [];
	const {
		createProject,
		createJudge,
		createCode,
		createDataset: submitCreateDataset,
		updateProjectTitle,
		updateCodeTitle,
		updateJudgeTitle,
	} = useWorkspaceSidebarActions();

	const handleCreateDataset = (
		name: string,
		datasetKind: "behavior_examples" | "operational_rules",
	) => {
		submitCreateDataset({ name, dataset_kind: datasetKind });
	};

	const subscribeToErrorLogs = (onNewLog?: (log: ErrorLog) => void) => {
		const channel = supabase
			.channel("error-logs-changes")
			.on(
				"postgres_changes",
				{
					event: "INSERT",
					schema: "public",
					table: "error_logs",
				},
				(payload: { new: ErrorLog }) => {
					const newLog = payload.new;
					toast({
						title: "New Error Logged",
						description: newLog.error_message,
						variant: "destructive",
					});
					onNewLog?.(newLog);
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	};

	const filteredLogs = useMemo(() => {
		let filtered = [...logs];

		if (searchTerm) {
			filtered = filtered.filter(
				(log) =>
					log.error_message.toLowerCase().includes(searchTerm.toLowerCase()) ||
					log.error_type.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}

		if (severityFilter !== "all") {
			filtered = filtered.filter((log) => log.severity === severityFilter);
		}

		if (sourceFilter !== "all") {
			filtered = filtered.filter((log) => log.source === sourceFilter);
		}

		if (resolvedFilter !== "all") {
			filtered = filtered.filter((log) =>
				resolvedFilter === "resolved" ? log.resolved : !log.resolved,
			);
		}

		return filtered;
	}, [logs, resolvedFilter, searchTerm, severityFilter, sourceFilter]);

	const unresolvedCount = useMemo(
		() => logs.filter((log) => !log.resolved).length,
		[logs],
	);

	const isPending = navigation.state !== "idle" || revalidator.state !== "idle";
	const isResolveAllPending = resolveAllFetcher.state !== "idle";
	const isResolveAllDisabled = unresolvedCount === 0 || isResolveAllPending;
	const resolveAllLabel =
		unresolvedCount > 0
			? `Mark All Resolved (${unresolvedCount})`
			: "Mark All Resolved";

	useEffect(() => {
		if (!user) return;
		const unsubscribe = subscribeToErrorLogs(() => {
			revalidator.revalidate();
		});
		return () => {
			unsubscribe?.();
		};
	}, [user, revalidator]);

	const handleResolveSubmit = (event: FormEvent<HTMLFormElement>) => {
		const formData = new FormData(event.currentTarget);
		const logId = formData.get("logId");
		if (typeof logId === "string") {
			setSubmittedLogId(logId);
		}
	};

	const handleResolveAll = () => {
		if (isResolveAllDisabled) return;

		const formData = new FormData();
		formData.append("intent", "resolve-all");
		const submissionToken = Date.now();
		setResolveAllSubmissionToken(submissionToken);
		resolveAllFetcher.submit(formData, {
			method: "post",
			action: "/error-logs",
		});
	};

	const exportLogs = () => {
		const dataStr = JSON.stringify(filteredLogs, null, 2);
		const dataBlob = new Blob([dataStr], { type: "application/json" });
		const url = URL.createObjectURL(dataBlob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `error-logs-${format(new Date(), "yyyy-MM-dd")}.json`;
		link.click();
	};

	const getSeverityColor = (severity: string) => {
		switch (severity) {
			case "critical":
				return "bg-destructive text-destructive-foreground";
			case "error":
				return "bg-red-500 text-white";
			case "warning":
				return "bg-yellow-500 text-white";
			case "info":
				return "bg-blue-500 text-white";
			default:
				return "bg-muted";
		}
	};

	const formatStructuredData = (value: unknown): string => {
		if (typeof value === "string") {
			return value;
		}

		try {
			return JSON.stringify(value, null, 2);
		} catch {
			return String(value);
		}
	};

	const renderStructuredSection = (label: string, value: unknown) => {
		if (value === null || value === undefined) {
			return null;
		}

		return (
			<div>
				<h4 className="font-semibold mb-2">{label}</h4>
				<pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
					{formatStructuredData(value)}
				</pre>
			</div>
		);
	};

	const uniqueSources = useMemo(
		() => Array.from(new Set(logs.map((log) => log.source))),
		[logs],
	);

	useEffect(() => {
		if (fetcher.state !== "idle" || !fetcher.data) {
			return;
		}

		const responseLogId = fetcher.data.logId ?? submittedLogId;
		if (!responseLogId) {
			return;
		}

		if (lastHandledLogIdRef.current === responseLogId) {
			return;
		}

		if (fetcher.data.ok) {
			lastHandledLogIdRef.current = responseLogId;
			toast({
				title: "Success",
				description: "Error marked as resolved",
			});
			revalidator.revalidate();
			setSubmittedLogId(null);
		} else if (fetcher.data.error) {
			lastHandledLogIdRef.current = responseLogId;
			toast({
				title: "Error",
				description: fetcher.data.error,
				variant: "destructive",
			});
			setSubmittedLogId(null);
		}
	}, [fetcher.state, fetcher.data, revalidator, submittedLogId]);

	useEffect(() => {
		if (resolveAllFetcher.state !== "idle" || !resolveAllFetcher.data) {
			return;
		}

		if (resolveAllSubmissionToken === null) {
			return;
		}

		if (lastResolveAllHandledTokenRef.current === resolveAllSubmissionToken) {
			return;
		}

		lastResolveAllHandledTokenRef.current = resolveAllSubmissionToken;

		if (resolveAllFetcher.data.ok) {
			const resolvedCount = resolveAllFetcher.data.resolvedCount ?? 0;
			toast({
				title: "Success",
				description:
					resolvedCount > 0
						? `Marked ${resolvedCount} error${resolvedCount === 1 ? "" : "s"} as resolved`
						: "No unresolved errors to resolve",
			});
			revalidator.revalidate();
		} else if (resolveAllFetcher.data.error) {
			toast({
				title: "Error",
				description: resolveAllFetcher.data.error,
				variant: "destructive",
			});
		}

		setResolveAllSubmissionToken(null);
	}, [
		resolveAllFetcher.state,
		resolveAllFetcher.data,
		resolveAllSubmissionToken,
		revalidator,
	]);

	return (
		<SidebarProvider>
			<div className="flex min-h-screen w-full">
				<WorkspaceSidebar
					projects={projects}
					judges={judges}
					codes={codes}
					datasets={datasets}
					onNewProject={createProject}
					onNewCode={createCode}
					onNewJudge={createJudge}
					onNewDataset={() => setCreateDatasetDialogOpen(true)}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onUpdateJudgeTitle={updateJudgeTitle}
					onOpenSettings={() => setSettingsOpen(true)}
				/>
				<main className="flex-1">
					<div className="border-b border-border p-2">
						<SidebarTrigger />
						{isPending && (
							<div className="h-0.5 bg-primary animate-pulse mt-2 rounded-full" />
						)}
					</div>
					<div className="container mx-auto p-6 space-y-6">
						<div className="flex items-center justify-between">
							<div>
								<h1 className="text-3xl font-bold">Error Logs</h1>
								<p className="text-muted-foreground">
									Monitor and manage system errors
								</p>
							</div>
							<div className="flex items-center gap-2">
								<Button
									onClick={handleResolveAll}
									disabled={isResolveAllDisabled}
									variant="default"
								>
									{isResolveAllPending ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Resolving…
										</>
									) : (
										resolveAllLabel
									)}
								</Button>
								<Button onClick={exportLogs} variant="outline">
									<Download className="mr-2 h-4 w-4" />
									Export
								</Button>
							</div>
						</div>

						<Card className="p-6">
							<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										placeholder="Search errors..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10"
									/>
								</div>

								<Select
									value={severityFilter}
									onValueChange={setSeverityFilter}
								>
									<SelectTrigger>
										<SelectValue placeholder="Severity" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All Severities</SelectItem>
										<SelectItem value="info">Info</SelectItem>
										<SelectItem value="warning">Warning</SelectItem>
										<SelectItem value="error">Error</SelectItem>
										<SelectItem value="critical">Critical</SelectItem>
									</SelectContent>
								</Select>

								<Select value={sourceFilter} onValueChange={setSourceFilter}>
									<SelectTrigger>
										<SelectValue placeholder="Source" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All Sources</SelectItem>
										{uniqueSources.map((source) => (
											<SelectItem key={source} value={source}>
												{source}
											</SelectItem>
										))}
									</SelectContent>
								</Select>

								<Select
									value={resolvedFilter}
									onValueChange={setResolvedFilter}
								>
									<SelectTrigger>
										<SelectValue placeholder="Status" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All Status</SelectItem>
										<SelectItem value="unresolved">Unresolved</SelectItem>
										<SelectItem value="resolved">Resolved</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Timestamp</TableHead>
											<TableHead>Severity</TableHead>
											<TableHead>Type</TableHead>
											<TableHead>Source</TableHead>
											<TableHead>Message</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredLogs.length === 0 ? (
											<TableRow>
												<TableCell
													colSpan={7}
													className="text-center text-muted-foreground"
												>
													No error logs found
												</TableCell>
											</TableRow>
										) : (
											filteredLogs.map((log) => (
												<TableRow key={log.id}>
													<TableCell className="text-sm">
														{format(
															new Date(log.created_at),
															"MMM dd, yyyy HH:mm:ss",
														)}
													</TableCell>
													<TableCell>
														<Badge className={getSeverityColor(log.severity)}>
															{log.severity}
														</Badge>
													</TableCell>
													<TableCell className="font-mono text-sm">
														{log.error_type}
													</TableCell>
													<TableCell>{log.source}</TableCell>
													<TableCell className="max-w-md truncate">
														{log.error_message}
													</TableCell>
													<TableCell>
														{log.resolved ? (
															<Badge
																variant="outline"
																className="text-green-600"
															>
																<CheckCircle className="mr-1 h-3 w-3" />
																Resolved
															</Badge>
														) : (
															<Badge
																variant="outline"
																className="text-orange-600"
															>
																<AlertCircle className="mr-1 h-3 w-3" />
																Unresolved
															</Badge>
														)}
													</TableCell>
													<TableCell>
														<Button
															variant="ghost"
															size="sm"
															onClick={() => setSelectedLog(log)}
														>
															View Details
														</Button>
													</TableCell>
												</TableRow>
											))
										)}
									</TableBody>
								</Table>
							</div>
						</Card>

						<Dialog
							open={!!selectedLog}
							onOpenChange={() => setSelectedLog(null)}
						>
							<DialogContent className="max-w-3xl max-h-[80vh]">
								<DialogHeader>
									<DialogTitle>Error Details</DialogTitle>
									<DialogDescription>
										{selectedLog &&
											format(new Date(selectedLog.created_at), "PPpp")}
									</DialogDescription>
								</DialogHeader>
								<ScrollArea className="h-[60vh]">
									{selectedLog && (
										<div className="space-y-4">
											<div>
												<h4 className="font-semibold mb-2">
													Error Information
												</h4>
												<div className="grid grid-cols-2 gap-4 text-sm">
													<div>
														<span className="text-muted-foreground">Type:</span>
														<p className="font-mono">
															{selectedLog.error_type}
														</p>
													</div>
													<div>
														<span className="text-muted-foreground">
															Severity:
														</span>
														<Badge
															className={getSeverityColor(selectedLog.severity)}
														>
															{selectedLog.severity}
														</Badge>
													</div>
													<div>
														<span className="text-muted-foreground">
															Source:
														</span>
														<p>{selectedLog.source}</p>
													</div>
													{selectedLog.http_status_code && (
														<div>
															<span className="text-muted-foreground">
																HTTP Status:
															</span>
															<p>{selectedLog.http_status_code}</p>
														</div>
													)}
												</div>
											</div>

											<div>
												<h4 className="font-semibold mb-2">Error Message</h4>
												<p className="text-sm bg-muted p-3 rounded">
													{selectedLog.error_message}
												</p>
											</div>

											{selectedLog.error_details && (
												<div>
													<h4 className="font-semibold mb-2">Error Details</h4>
													<pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
														{selectedLog.error_details}
													</pre>
												</div>
											)}

											{renderStructuredSection("Context", selectedLog.context)}

											{renderStructuredSection(
												"Request Payload",
												selectedLog.request_payload,
											)}

											{renderStructuredSection(
												"Response Payload",
												selectedLog.response_payload,
											)}

											{!selectedLog.resolved && (
												<fetcher.Form
													method="post"
													onSubmit={handleResolveSubmit}
													className="w-full"
												>
													<input type="hidden" name="intent" value="resolve" />
													<input
														type="hidden"
														name="logId"
														value={selectedLog.id}
													/>
													<Button
														type="submit"
														className="w-full"
														disabled={
															fetcher.state !== "idle" &&
															submittedLogId === selectedLog.id
														}
													>
														{fetcher.state !== "idle" &&
														submittedLogId === selectedLog.id ? (
															<>
																<Loader2 className="mr-2 h-4 w-4 animate-spin" />
																Resolving…
															</>
														) : (
															"Mark as Resolved"
														)}
													</Button>
												</fetcher.Form>
											)}
										</div>
									)}
								</ScrollArea>
							</DialogContent>
						</Dialog>
					</div>
				</main>
				<CreateDatasetDialog
					open={createDatasetDialogOpen}
					onOpenChange={setCreateDatasetDialogOpen}
					onSubmit={handleCreateDataset}
				/>
				<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
			</div>
		</SidebarProvider>
	);
}

export function ErrorLogsErrorBoundary() {
	const error = useRouteError();
	const message = isRouteErrorResponse(error)
		? `${error.status} ${error.statusText}`
		: error instanceof Error
			? error.message
			: "Something went wrong while loading error logs.";

	return (
		<SidebarProvider>
			<div className="flex min-h-screen w-full">
				<main className="flex-1 flex items-center justify-center p-8">
					<Card className="max-w-lg w-full p-6 space-y-4">
						<div className="flex items-center space-x-3">
							<AlertCircle className="text-destructive h-5 w-5" />
							<div>
								<h1 className="text-xl font-semibold">
									Error Logs Unavailable
								</h1>
								<p className="text-muted-foreground text-sm">{message}</p>
							</div>
						</div>
						<p className="text-sm text-muted-foreground">
							Try refreshing the page. If the issue persists, check Supabase
							connectivity or recent migrations.
						</p>
						<Button onClick={() => window.location.reload()} variant="outline">
							Reload
						</Button>
					</Card>
				</main>
			</div>
		</SidebarProvider>
	);
}
